import { Button } from '@headlessui/react';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import type { DetailedHTMLProps, ForwardedRef, InputHTMLAttributes, ReactNode, MouseEvent } from 'react';
import { forwardRef, useId } from 'react';
import InputGroup from './InputGroup';
import Field, { type FieldProps } from '@/components/common/Field';

export type InputProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> &
    Omit<FieldProps, 'id' | 'children'> & {
        inputState?: 'valid' | 'invalid' | 'normal';
        prefixElement?: ReactNode;
        suffixElement?: ReactNode;
        onSuffixClick?: (e: MouseEvent<HTMLButtonElement>) => void;
        inputClassName?: string;
    };

const InputWithRef = (
    {
        id,
        label,
        description,
        labelExtra,
        className,
        helpElement,
        inputState = 'normal',
        errorElement,
        prefixElement,
        suffixElement: suffixElementFromProps,
        onSuffixClick,
        required,
        labelSize,
        labelWeight,
        labelCapitalize,
        inputClassName,
        ...props
    }: InputProps,
    ref: ForwardedRef<HTMLInputElement>
) => {
    const elementId = useId();
    const computedId = id || elementId;
    let suffixElement = suffixElementFromProps;
    const inputTextSize = 'text-base';
    const inputLeading = 'leading-6';
    const inputFontWeight = 'font-medium';

    if (inputState === 'invalid') {
        suffixElement = <ExclamationCircleIcon aria-hidden="true" className="size-5 text-red-500" />;
    } else if (inputState === 'valid') {
        suffixElement = <CheckCircleIcon aria-hidden="true" className="size-5 text-green-500" />;
    }

    const input = (
        <InputGroup disabled={props.disabled} inputState={inputState}>
            {prefixElement && (
                <div
                    className={clsx(
                        'shrink-0 text-base font-medium select-none',
                        props.disabled ? 'text-neutral-500' : 'text-neutral-800'
                    )}
                >
                    {prefixElement}
                </div>
            )}
            <input
                ref={ref}
                data-with-prefix={!!prefixElement || undefined}
                data-with-suffix={!!suffixElement || undefined}
                className={clsx(
                    // base
                    'w-full grow border-0 px-0 py-[7px] text-neutral-800 shadow-none placeholder:text-neutral-400',
                    inputTextSize,
                    inputLeading,
                    inputFontWeight,
                    // focus
                    'focus:ring-0',
                    // margins for prefix and suffix
                    'data-with-prefix:pl-1 data-with-suffix:pr-1',
                    // disable state
                    'disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-500',
                    inputClassName
                )}
                type={props.type || 'text'}
                id={computedId}
                required={required}
                {...props}
            />
            {suffixElement &&
                (onSuffixClick ? (
                    <Button
                        type="button"
                        className="inset-y-0 flex shrink-0 cursor-pointer items-center px-0 text-gray-400 shadow-inherit hover:text-gray-500"
                        onClick={onSuffixClick}
                    >
                        {suffixElement}
                    </Button>
                ) : (
                    <div
                        className={clsx(
                            'shrink-0 text-base font-medium select-none',
                            props.disabled ? 'text-neutral-500' : 'text-neutral-800'
                        )}
                    >
                        {suffixElement}
                    </div>
                ))}
        </InputGroup>
    );

    return (
        <Field
            name={props.name}
            id={computedId}
            label={label}
            description={description}
            errorElement={errorElement}
            helpElement={helpElement}
            labelExtra={labelExtra}
            required={required}
            labelSize={labelSize}
            labelWeight={labelWeight}
            labelCapitalize={labelCapitalize}
            disabled={props.disabled}
        >
            {input}
        </Field>
    );
};

const Input = forwardRef(InputWithRef);

export default Input;
